import { Library } from "@observablehq/runtime";
import htm from "htm";
import React from "react";
import ReactDOM from "react-dom/client";

import { type PageStorageBridge } from "@moment/api-collab/page-local-storage/client";
import { createCellID } from "@moment/api-collab/prosemirror-utils";
import {
	CellServicesContextProvider,
	useCellServices,
} from "@moment/design-system/CellServicesContext";
import * as DesignSystemThemeProvider from "@moment/design-system/ThemeProvider";

import { type CanvasRuntimeManager } from "~/components/canvas/easel/runtime";
import { VariableObserver } from "~/components/canvas/easel/runtime/VariableObserver";
import {
	type VariableObserverType,
	makeUseCellValue,
} from "~/components/canvas/easel/runtime/makeUseCellValue";
import { snippetFromExpression } from "~/components/canvas/easel/runtime/snippetFromExpression";
import { define } from "~/runtime/canvas-std-lib/react";
import { createAtlasProxyFetch } from "~/utils/atlas";
import type { Env } from "~/utils/common";

import { getBlockedWindowProperties, getSafeWindow } from "./safeWindow";

export type MomentStdLibOptions = {
	runtime: CanvasRuntimeManager;
	env: Env;
	pageStorage: PageStorageBridge;
	documentInteractionMode: "static" | "interactive";
	currentUser: { id: string; name: string; profilePicture: string } | null;
};

export const momentStdLib = (options: MomentStdLibOptions) => {
	const stdLib = define(React, ReactDOM, htm);

	// This function defines builtin values for observable runtime to use.
	//
	// Unlike variables, builtins cannot depend on the value of other variables or builtins; they
	// are defined with no inputs.
	//
	// Important: If a builtin is defined as a function, it will be invoked lazily to determine the
	// value of the builtin. If you wish for the value of a builtin to be a function, the builtin
	// must be defined either as a promise that resolves to a function or as a function that returns
	// a function. Builtins may also be defined as generators for dynamic values.
	//
	// reference: https://github.com/observablehq/runtime#runtimes

	return Object.assign(new Library(), {
		currentUser: options.currentUser,

		// This is the pageStorage for this specific page
		pageStorage: async function* pageStorage() {
			//
			// [MOM-7169] DO NOT CHANGE `pageStorage` UNLESS YOU KNOW WHAT YOU ARE DOING.
			//
			// The runtime has a bug that causes `Promise.resolve(options.pageStorage.data)` to
			// sometimes terminate at unexpected times, which will look to users like a halt to
			// updates from page storage.
			//
			// This was INCREDIBLY PAINFUL TO DEBUG, so if you change this run it by Alex.
			//

			yield* options.pageStorage.data;
		},
		pageStorageCommit: Promise.resolve(options.pageStorage.commit),

		atlasProxyFetch: Promise.resolve(createAtlasProxyFetch()),

		CellServicesContextProvider: Promise.resolve(CellServicesContextProvider),
		useCellServices: Promise.resolve(useCellServices),
		documentInteractionMode: options.documentInteractionMode,

		DesignSystemThemeProvider,

		ReactErrorBoundary: import("react-error-boundary"),
		// moment libaries
		// react
		React: Object.assign({ ...React }, stdLib),
		std: Promise.resolve(stdLib),
		render: Promise.resolve(stdLib.render),
		jsx: Promise.resolve(stdLib.jsx),
		memo: Promise.resolve(stdLib.memo),
		forwardRef: Promise.resolve(stdLib.forwardRef),
		component: Promise.resolve(stdLib.component),
		useCellValue: Promise.resolve(
			makeUseCellValue(
				React,
				createCellID,
				options.runtime,
				snippetFromExpression,
				VariableObserver as unknown as VariableObserverType
			)
		),
		dateFns: import("date-fns"),
		designSystem: import(/* webpackChunkName: 'designSystem' */ "./designSystem").then(
			({ designSystem }) => designSystem
		),

		// Overriden Global Variables
		window: getSafeWindow(),
		...getBlockedWindowProperties(),
	});
};
